/**
 * The edit session around a cabinet rebuild (open editor -> live changes ->
 * save / cancel): readiness / target matching, original-dimension snapshots,
 * and the deselect / re-select / persist steps that run after the mesh has
 * been re-rendered. The mesh rebuild itself lives in `rebuildCabinet.ts`.
 */
import {MutableRefObject} from 'react';
import * as THREE from 'three';
import {PreviewFormValues} from 'components/customer/Preview3D/types';
import {
    CabinetMesh,
    RoomPlannerProduct,
} from 'components/customer/RoomPlanner/types';
import {
    getAttachedSideFillersAndVermins,
    SideAttachment,
} from 'components/customer/RoomPlanner/helpers/sideAttachmentReposition';
import productEvents from 'components/customer/RoomPlanner/lib/useProductEvents';
import {selectCabinet} from 'components/customer/RoomPlanner/lib/useCabinetSelect';
import {autoSave} from 'components/customer/RoomPlanner/lib/useAutoSave';
import {setBackupProductValues} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {AppDispatch} from 'store/customer/storeSetup';
import {EditSessionOriginalRefs, PreviousCabinetDimensions} from './types';

/**
 * Whether the live form update has enough data to rebuild a cabinet.
 * @param {object | null | undefined} productValuesForUpdate - Form update payload
 * @param {object | null} selectedProductValue - Currently selected product
 * @param {Array | null | undefined} cabinets - Scene cabinets
 * @return {boolean} True when the update can run
 */
export const isCabinetUpdateReady = (
    productValuesForUpdate: PreviewFormValues | null | undefined,
    selectedProductValue: RoomPlannerProduct | null,
    cabinets: CabinetMesh[] | null | undefined
): productValuesForUpdate is PreviewFormValues => {
    return Boolean(
        productValuesForUpdate &&
        (selectedProductValue || productValuesForUpdate.sideEffectFromUpdate) &&
        cabinets
    );
};

/**
 * Cabinets whose `id` matches the form update (and the current selection,
 * unless switching / undo / side-effect).
 * @param {Array} cabinets - Scene cabinets
 * @param {object | null} selectedProductValue - Selected product
 * @param {object} productValuesForUpdate - Form update payload
 * @return {Array} Matching cabinets
 */
export const findCabinetsForProductUpdate = (
    cabinets: CabinetMesh[],
    selectedProductValue: RoomPlannerProduct | null,
    productValuesForUpdate: PreviewFormValues
): CabinetMesh[] => {
    const isSwitchingCabinet = productValuesForUpdate.isSwitchingCabinet;
    const fromUndoOrRedo = productValuesForUpdate.fromUndoOrRedo;

    return cabinets.filter(
        (group) =>
            (group.userData.values.id == selectedProductValue?.job_cabinet_id ||
                isSwitchingCabinet ||
                fromUndoOrRedo ||
                productValuesForUpdate?.sideEffectFromUpdate) &&
            group.userData.values.id == productValuesForUpdate.job_cabinet_id
    );
};

/**
 * Product values to rebuild from: mesh values for undo / side-effect, otherwise
 * the currently selected product.
 * @param {object | null} selectedProductValue - Selected product
 * @param {object} productValuesForUpdate - Form update payload
 * @param {Array} cabinets - Matching cabinets
 * @return {object | null} Product to merge into the mesh
 */
export const resolveProductValueForUpdate = (
    selectedProductValue: RoomPlannerProduct | null,
    productValuesForUpdate: PreviewFormValues,
    cabinets: CabinetMesh[]
): RoomPlannerProduct | null => {
    const fromUndoOrRedo = productValuesForUpdate.fromUndoOrRedo;

    if (
        (fromUndoOrRedo || productValuesForUpdate.sideEffectFromUpdate) &&
        cabinets.length > 0
    ) {
        return cabinets[0].userData.values;
    }

    return selectedProductValue;
};

/**
 * Captures side-attached filler / vermin positions at the start of an edit
 * session so cancel can restore them.
 * @param {Array} attachedSideProducts - Side-attached fillers / vermin
 * @param {object} attachedSideOriginalPositionsRef - Snapshot ref
 * @param {boolean} overwrite - Replace an existing snapshot
 * @return {void}
 */
export const captureAttachedSideOriginalPositions = (
    attachedSideProducts: SideAttachment[],
    attachedSideOriginalPositionsRef: EditSessionOriginalRefs['attachedSideOriginalPositionsRef'],
    overwrite = false
) => {
    if (attachedSideProducts.length === 0) {
        return;
    }
    if (!overwrite && attachedSideOriginalPositionsRef.current != null) {
        return;
    }

    attachedSideOriginalPositionsRef.current = new Map(
        attachedSideProducts.map(({cabinet: attached}) => [
            attached.uuid,
            attached.position.clone(),
        ])
    );
};

/**
 * First form update after opening the editor only snapshots originals.
 * @param {object} params - Session flags and refs
 * @return {boolean} True when the caller should skip the mesh rebuild
 */
export const tryBeginCabinetUpdateSession = ({
    updateInitialized,
    isSwitchingCabinet,
    cabinet,
    allCabinets,
    attachedSideOriginalPositionsRef,
}: {
    updateInitialized: MutableRefObject<boolean>;
    isSwitchingCabinet?: boolean;
    cabinet: CabinetMesh;
    allCabinets: CabinetMesh[];
    attachedSideOriginalPositionsRef: EditSessionOriginalRefs['attachedSideOriginalPositionsRef'];
}): boolean => {
    if (updateInitialized.current || isSwitchingCabinet) {
        return false;
    }

    updateInitialized.current = true;
    captureAttachedSideOriginalPositions(
        getAttachedSideFillersAndVermins(cabinet, allCabinets),
        attachedSideOriginalPositionsRef
    );
    return true;
};

/**
 * Records original depth / width / height / thickness for the edit session.
 * @param {object} refs - Edit-session dimension refs
 * @param {object} previous - Dimensions read from the live mesh
 * @param {boolean | undefined} fromBackup - Skip when restoring a backup
 * @return {boolean} True when this is the first rebuild of the session
 */
export const captureEditSessionOriginals = (
    refs: EditSessionOriginalRefs,
    previous: PreviousCabinetDimensions,
    fromBackup?: boolean
): boolean => {
    const isStartingEditSession =
        refs.originalDepthRef?.current == null && !fromBackup;

    if (isStartingEditSession && refs.originalDepthRef) {
        refs.originalDepthRef.current = previous.previousCabinetDepth;
    }

    if (refs.originalExteriorThicknessRef.current == null && !fromBackup) {
        refs.originalExteriorThicknessRef.current =
            previous.previousExteriorThickness;
    }

    if (
        refs.originalHeightRef &&
        refs.originalHeightRef.current == null &&
        !fromBackup
    ) {
        refs.originalHeightRef.current = previous.previousCabinetHeight;
    }

    if (
        refs.originalWidthRef &&
        refs.originalWidthRef.current == null &&
        !fromBackup
    ) {
        refs.originalWidthRef.current = previous.previousCabinetWidth;
    }

    return Boolean(isStartingEditSession);
};

const clearEditSessionOriginals = (refs: EditSessionOriginalRefs) => {
    if (refs.originalDepthRef) {
        refs.originalDepthRef.current = null;
    }
    if (refs.originalHeightRef) {
        refs.originalHeightRef.current = null;
    }
    if (refs.originalWidthRef) {
        refs.originalWidthRef.current = null;
    }
    refs.originalExteriorThicknessRef.current = null;
    refs.attachedSideOriginalPositionsRef.current = null;
};

/**
 * Deselects the outgoing mesh shortly after a rebuild starts.
 * @param {CabinetMesh} cabinet - Cabinet about to be replaced
 * @return {void}
 */
export const scheduleCabinetDeselect = (cabinet: CabinetMesh) => {
    setTimeout(() => {
        productEvents.triggerEvents(cabinet, 'onClick', false);
    }, 100);
};

/**
 * Re-selects the rebuilt cabinet, stores a backup when switching, and
 * revalidates overlaps.
 * @param {object} params - Rebuilt mesh and UI callbacks
 * @return {void}
 */
export const scheduleCabinetUpdateUi = ({
    updatedCabinetGroup,
    previousCabinet,
    cabinets,
    isSwitchingCabinet,
    productValue,
    renderer,
    camera,
    dispatch,
    setSelectedCabinetGroup,
    checkIfOverlapsOtherCabinets,
}: {
    updatedCabinetGroup: CabinetMesh;
    previousCabinet: CabinetMesh;
    cabinets: CabinetMesh[];
    isSwitchingCabinet?: boolean;
    productValue: RoomPlannerProduct;
    renderer?: THREE.WebGLRenderer;
    camera?: THREE.OrthographicCamera;
    dispatch: AppDispatch;
    setSelectedCabinetGroup: (cabinet: CabinetMesh) => void;
    checkIfOverlapsOtherCabinets: (
        currentCabinet: THREE.Group,
        cabinetList: THREE.Group[],
        onOverlap: (value: boolean) => void
    ) => void;
}) => {
    setTimeout(() => {
        // Read the live URL rather than the `searchParams` captured when this
        // update was scheduled, since a save completing (e.g. via the "Save
        // Changes" dialog) can clear preventReset before this callback runs.
        // The placeholder editor also sets preventReset, so the cabinet id
        // has to match or this restore snaps selection back onto the
        // previous cabinet.
        const liveParams = new URLSearchParams(window.location.search);
        const editingCabinetId = liveParams.get('cabinetId');
        const values = updatedCabinetGroup?.userData?.values;
        const isEditingMode =
            liveParams.get('preventReset') === 'true' &&
            !!editingCabinetId &&
            (editingCabinetId == String(values?.job_cabinet_id ?? '') ||
                editingCabinetId == String(values?.id ?? ''));

        if (isEditingMode && !isSwitchingCabinet) {
            productEvents.triggerEvents(updatedCabinetGroup, 'onClick', false);
            setSelectedCabinetGroup(updatedCabinetGroup);
            if (renderer && camera) {
                selectCabinet(updatedCabinetGroup, camera, renderer);
            }
        } else if (isSwitchingCabinet) {
            productEvents.triggerEvents(updatedCabinetGroup, 'onClick', true);
            dispatch(setBackupProductValues(productValue));
        }
        checkIfOverlapsOtherCabinets(previousCabinet, cabinets, () => {
            productEvents.triggerEvents(previousCabinet, 'onOverlap', true);
            productEvents.revalidateOverlaps();
        });
    }, 100);
};

/**
 * Persists the rebuilt cabinet and attached products after Save, and clears
 * the edit-session originals.
 * @param {object} productValuesForUpdate - Form update payload
 * @param {object} refs - Edit-session dimension refs
 * @param {CabinetMesh} updatedCabinetGroup - Re-rendered cabinet
 * @param {Array} attachedSideProducts - Side-attached fillers / vermin
 * @return {void}
 */
export const persistCabinetUpdateOnSave = (
    productValuesForUpdate: PreviewFormValues,
    refs: EditSessionOriginalRefs,
    updatedCabinetGroup: CabinetMesh,
    attachedSideProducts: SideAttachment[]
) => {
    if (!productValuesForUpdate.isSavingProduct) {
        return;
    }

    clearEditSessionOriginals(refs);
    autoSave(updatedCabinetGroup, true);
    attachedSideProducts.forEach(({cabinet: attachedCabinet}) => {
        autoSave(attachedCabinet, true);
    });
};

/**
 * Re-applies the current door / drawer open state without animating.
 * @param {CabinetMesh} updatedCabinetGroup - Re-rendered cabinet
 * @return {void}
 */
export const syncCabinetDoorAfterUpdate = (
    updatedCabinetGroup: CabinetMesh
) => {
    const product = updatedCabinetGroup.userData.values;
    productEvents.triggerEvents(updatedCabinetGroup, 'toggleDoor', {
        doorGap: product.cabinet_door_gap,
        isDoorOrDrawerOpen: updatedCabinetGroup.userData.isDoorOrDrawerOpen,
        animationSpeed: 0,
    });
};
